import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { TrendingUp, TrendingDown, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfitLossTab = ({ getAuthHeader, fromDate, toDate }) => {
  const [loading, setLoading] = useState(false);
  const [profitLoss, setProfitLoss] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchProfitLoss = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/accounting/reports/profit-loss?from_date=${fromDate}&to_date=${toDate}`,
        { headers: getAuthHeader() }
      );
      setProfitLoss(res.data);
    } catch (error) {
      toast.error('Failed to fetch P&L report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitLoss();
  }, [fromDate, toDate]);

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/profit-loss/pdf?from_date=${fromDate}&to_date=${toDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `profit_loss_${fromDate}_${toDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/reports/profit-loss/excel?from_date=${fromDate}&to_date=${toDate}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `profit_loss_${fromDate}_${toDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Profit & Loss Statement</CardTitle>
          <CardDescription>
            For the period {fromDate} to {toDate}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pl-pdf">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-pl-excel">
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : profitLoss ? (
          <div className="space-y-6">
            {/* Income Section */}
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Income
              </h3>
              <Table>
                <TableBody>
                  {profitLoss.income?.items?.filter(i => i.amount !== 0).map((item) => (
                    <TableRow key={item.account_code}>
                      <TableCell>{item.account_code}</TableCell>
                      <TableCell>{item.account_name}</TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-green-50">
                    <TableCell colSpan={2}>Total Income</TableCell>
                    <TableCell className="text-right text-green-700">
                      {formatCurrency(profitLoss.income?.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Expenses Section */}
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center">
                <TrendingDown className="h-5 w-5 mr-2" />
                Expenses
              </h3>
              <Table>
                <TableBody>
                  {profitLoss.expenses?.items?.filter(i => i.amount !== 0).map((item) => (
                    <TableRow key={item.account_code}>
                      <TableCell>{item.account_code}</TableCell>
                      <TableCell>{item.account_name}</TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-bold bg-red-50">
                    <TableCell colSpan={2}>Total Expenses</TableCell>
                    <TableCell className="text-right text-red-700">
                      {formatCurrency(profitLoss.expenses?.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Net Profit/Loss */}
            <div className={`p-4 rounded-lg ${profitLoss.is_profit ? 'bg-green-100' : 'bg-red-100'}`}>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">
                  {profitLoss.is_profit ? 'Net Profit' : 'Net Loss'}
                </span>
                <span className={`text-2xl font-bold ${profitLoss.is_profit ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(Math.abs(profitLoss.net_profit_loss))}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center py-8 text-muted-foreground">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfitLossTab;
